import React from "react";
import { View, TouchableOpacity, StyleSheet, Modal } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import Svg, { Path } from "react-native-svg";
import Text from "@components/CustomText";
import CloseIcon from "@assets/svg/CloseIcon";
import colors from "@constants/color";
import { create, normalize } from "@utils/metrics_1";

export interface CompleteDeliveryData {
  delivery_success_text: string;
  title: string;
  amount: string;
  breakdown: { title: string; value: string }[];
}

interface CompleteDeliveryProps {
  isVisible: boolean;
  onClose: () => void;
  completeDeliveryData?: CompleteDeliveryData;
}

export const DEFAULT_COMPLETE_DELIVERY_DATA: CompleteDeliveryData = {
  delivery_success_text: "डिलीवरी पूरी हुई",
  title: "ट्रिप अर्निंग्स",
  amount: "",
  breakdown: [],
};

const CompleteDelivery: React.FC<CompleteDeliveryProps> = ({
  isVisible,
  onClose,
  completeDeliveryData = DEFAULT_COMPLETE_DELIVERY_DATA,
}) => (
  <Modal
    visible={isVisible}
    animationType="slide"
    presentationStyle="fullScreen"
    onRequestClose={onClose}
    statusBarTranslucent
    navigationBarTranslucent
  >
    <SafeAreaView style={styles.container} edges={["top", "bottom"]}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={onClose}
          style={styles.closeButton}
          testID="complete-delivery-close-button"
        >
          <CloseIcon fill={colors.white} width={24} height={24} />
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        <View style={styles.checkmarkContainer}>
          <View style={styles.checkmarkCircle}>
            <View style={styles.checkmarkInnerCircle}>
              <Svg width={40} height={40} viewBox="0 0 24 24" fill="none">
                <Path
                  d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                  fill={colors.white}
                />
              </Svg>
            </View>
          </View>
          <Text style={styles.successText}>
            {completeDeliveryData.delivery_success_text}
          </Text>
        </View>

        <View style={styles.infoCard}>
          <View style={styles.earningsSection}>
            <Text style={styles.earningsLabel}>
              {completeDeliveryData.title}
            </Text>
            <Text style={styles.earningsAmount}>
              {completeDeliveryData.amount}
            </Text>
          </View>
          <View style={styles.detailsSection}>
            {(completeDeliveryData.breakdown || []).map((item, index) => (
              <View style={styles.detailRow} key={index}>
                <Text style={styles.detailLabel}>{item.title}</Text>
                <Text style={styles.detailValue}>{item.value}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    </SafeAreaView>
  </Modal>
);

const styles = create({
  container: {
    flex: 1,
    backgroundColor: colors.plain_green,
  },
  header: {
    paddingTop: 8,
    paddingLeft: 16,
    paddingBottom: 8,
  },
  closeButton: {
    padding: 8,
    alignSelf: "flex-start",
  },
  content: {
    flex: 1,
    paddingTop: 40,
    paddingHorizontal: 20,
  },
  checkmarkContainer: {
    alignItems: "center",
    marginBottom: 40,
  },
  checkmarkCircle: {
    width: 160,
    height: 160,
    borderRadius: 9999,
    backgroundColor: colors.white,
    borderWidth: 8,
    borderColor: colors.plain_green,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 24,
  },
  checkmarkInnerCircle: {
    width: 80,
    height: 80,
    borderRadius: 9999,
    backgroundColor: colors.plain_green,
    justifyContent: "center",
    alignItems: "center",
  },
  successText: {
    fontSize: normalize(20),
    color: colors.white,
    fontWeight: "700",
  },
  infoCard: {
    backgroundColor: colors.white,
    borderRadius: 24,
    padding: 8,
    width: "100%",
  },
  earningsSection: {
    alignItems: "center",
    marginBottom: 8,
    backgroundColor: colors.lightGray,
    borderRadius: 12,
    padding: 8,
  },
  earningsLabel: {
    fontSize: normalize(18),
    fontWeight: "700",
    color: colors.black,
    marginBottom: 4,
  },
  earningsAmount: {
    fontSize: normalize(36),
    color: colors.plain_green,
    fontWeight: "700",
  },
  detailsSection: {
    marginTop: 4,
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    paddingHorizontal: 12,
  },
  detailLabel: {
    fontSize: normalize(16),
    color: colors.black,
  },
  detailValue: {
    fontSize: normalize(16),
    color: colors.black,
  },
}) as ReturnType<typeof StyleSheet.create>;

export default CompleteDelivery;
